import {
  FC,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
  DragEvent,
} from 'react';
import { useTranslate } from 'hooks/useTranslate';

import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import InputText from 'modules/_shared/components/InputText';
import { MessageType } from 'modules/_shared/components/InputMessage';
import MandatoryField from 'modules/_shared/components/MandatoryField';

import validateIBAN from 'modules/trading/utils/validateIBAN';

import { usePublishTradingOfferModal } from '../context/PublishTradingOfferModalContext';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

const PublishTradingOfferModalStep2: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = usePublishTradingOfferModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [ibanError, setIbanError] = useState('');

  const partnerAddress = data.publisherPartner?.address;
  const isSellOffer = data.tradingOffer?.type === 'SELL';
  const societyId = data.tradingOffer?.society
    ? String(data.tradingOffer.society)
    : '';

  const handleBankAccountChange = useCallback(
    (value: string) => {
      actions.handleUpdatePublisherPartnerField('bankAccountNumber', value);
      if (ibanError) {
        setIbanError('');
      }
    },
    [actions, ibanError]
  );

  const handleBankAccountBlur = useCallback(() => {
    const bankAccount = data.publisherPartner?.bankAccountNumber || '';
    if (!bankAccount) {
      setIbanError('');
      return;
    }
    const { isValid, error } = validateIBAN(bankAccount);
    setIbanError(isValid ? '' : error || '');
  }, [data.publisherPartner?.bankAccountNumber]);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files) {
        Array.from(files).forEach((file) =>
          actions.handleAddPublisherDocument(file)
        );
      }
      e.target.value = '';
    },
    [actions]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const { files } = e.dataTransfer;
      if (files) {
        Array.from(files).forEach((file) =>
          actions.handleAddPublisherDocument(file)
        );
      }
    },
    [actions]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-4">
        <em className="icon ni ni-shield-check fs-24px text-primary" />
        <h5 className="mt-2">{t('DataVerification')}</h5>
        <p className="text-soft">{t('ConfirmYourDataToCompleteOperation')}</p>
      </div>

      <div className="row">
        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('PersonalData')}</h6>
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-name"
            label={t('FullName')}
            value={data.publisherPartner?.name || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('name', value)
            }
            required
            lgSize={false}
            tooltip={t('FullNameAsInIdentityDocument')}
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-cif"
            label={t('NifNie')}
            value={data.publisherPartner?.cif || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('cif', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-email"
            label={t('Email')}
            value={data.publisherPartner?.email || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('email', value)
            }
            required
            lgSize={false}
            emailInput
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-phone"
            label={t('Telephone')}
            value={data.publisherPartner?.phone || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('phone', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('FiscalResidence')}</h6>
        </div>

        <div className="col-12 mb-3">
          <InputText
            id="publish-offer-publisher-address-line1"
            label={t('FullAddress')}
            value={partnerAddress?.line1 || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('line1', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="publish-offer-publisher-address-zip"
            label={t('PostalCode')}
            value={partnerAddress?.zip || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('zip', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="publish-offer-publisher-address-city"
            label={t('City')}
            value={partnerAddress?.city || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('city', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="publish-offer-publisher-address-country"
            label={t('Country')}
            value={partnerAddress?.country || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('country', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('BankInformation')}</h6>
        </div>

        <div className="col-12 mb-3">
          <InputText
            id="publish-offer-publisher-bank-account"
            label={t('BankAccountNumber')}
            value={data.publisherPartner?.bankAccountNumber || ''}
            onChange={handleBankAccountChange}
            onBlur={handleBankAccountBlur}
            required
            lgSize={false}
            additionalInfo={ibanError || undefined}
            additionalInfoType={ibanError ? MessageType.ERROR : undefined}
          />
        </div>

        <div className="col-12 mb-2">
          <h6 className="overline-title text-soft">{t('Documentation')}</h6>
        </div>

        <div className="col-12">
          <div className="form-group">
            <label className="form-label d-flex align-items-center">
              {t('AttachIdentityDocument')}
              <MandatoryField className="ml-1" />
              <IconTooltip
                id="publish-offer-identity-doc-tooltip"
                tooltip={t('IdentityDocumentTooltip')}
                place="right"
                className="fs-24px text-soft"
              />
            </label>
            <div
              className="upload-zone border border-dashed rounded p-4 text-center dropzone dz-clickable"
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUploadClick();
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <em className="icon ni ni-upload-cloud fs-24px text-soft" />
              <p className="text-soft mb-2">{t('DragDocumentHereOr')}</p>
              <button
                type="button"
                className="btn btn-dim btn-sm btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadClick();
                }}
              >
                {t('SelectFile')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileSelect}
              />
              <p className="text-soft fs-11px mt-2 mb-0">
                {t('IdentityDocumentFormats')}
              </p>
            </div>

            {data.publisherDocuments.length > 0 && (
              <div className="mt-2">
                {data.publisherDocuments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="d-flex align-items-center justify-content-between py-1 px-2 bg-light rounded mb-1"
                  >
                    <span className="text-soft fs-12px text-truncate flex-grow-1 mr-2">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-outline-danger"
                      onClick={() => actions.handleRemovePublisherDocument(index)}
                      aria-label={t('Remove')}
                    >
                      <em className="icon ni ni-trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 mt-2">
          {isSellOffer && (
            <InputCheckbox
              id="publish-offer-hide-seller-name-checkbox"
              label={t('DoNotShowSellerName')}
              checked={data.isHideSellerName}
              onChange={actions.handleChangeHideSellerName}
              marginBottom={2}
            />
          )}

          <div className="custom-control custom-checkbox mb-2">
            <input
              type="checkbox"
              className="custom-control-input"
              id="publish-offer-accept-terms-checkbox"
              checked={data.isTermsAndConditionsAccepted}
              onChange={actions.handleChangeTermsAndConditionsAcceptance}
            />
            <label
              className="custom-control-label"
              htmlFor="publish-offer-accept-terms-checkbox"
            >
              {t('ConfirmDataAndAcceptTerms')}{' '}
              <a
                href={`/#/condiciones-ofertas/${societyId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('EquityMarketTermsAndConditions')}
              </a>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishTradingOfferModalStep2;
